import React, { useState, useEffect, useRef, useCallback } from 'react';
import { UseFormRegister, FieldErrorsImpl, FieldValues, UseFormSetValue } from 'react-hook-form';
import { Error } from '../Error';
import { Width } from '../Width';

import classes from './index.module.scss';
import { useTranslations } from 'next-intl';

export interface CaptchaField {
  name: string;
  label?: string;
  width?: number | string;
  required?: boolean;
  blockType?: 'captcha';
}

export const Captcha: React.FC<
  CaptchaField & {
    register: UseFormRegister<FieldValues & any>;
    setValue: UseFormSetValue<FieldValues & any>;
    errors: Partial<
      FieldErrorsImpl<{
        [x: string]: any;
      }>
    >;
  }
> = ({ name, label, width, register, setValue, required: requiredFromProps, errors }) => {
  const t = useTranslations('forms');
  const [isSolved, setIsSolved] = useState(false);
  const [isSolving, setIsSolving] = useState(false);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [challengeData, setChallengeData] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const workerRef = useRef<Worker | null>(null);

  // Clean up worker on unmount
  useEffect(() => {
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, []);

  // Fetch challenge on mount
  useEffect(() => {
    const fetchChallenge = async () => {
      try {
        const response = await fetch('/next/captcha/challenge/');
        if (!response.ok) {
          setError('Failed to load verification challenge');
          setIsLoading(false);
          return;
        }
        const data = await response.json();
        setChallengeId(data.id);
        setChallengeData(data.challenge);
        setIsLoading(false);
      } catch (err) {
        console.error('[CAPTCHA] Failed to fetch challenge:', err);
        setError('Failed to load verification');
        setIsLoading(false);
      }
    };

    fetchChallenge();
  }, []);

  const solveChallenge = useCallback(async () => {
    if (!challengeId || !challengeData || isSolving || isSolved) return;

    setIsSolving(true);

    try {
      // Dynamically import the worker to avoid SSR issues
      const { worker } = await import('@p-captcha/js');
      workerRef.current = worker;

      worker.onmessage = (event: MessageEvent<string>) => {
        const answer = event.data;
        const captchaPayload = JSON.stringify({
          id: challengeId,
          answer,
        });
        setIsSolved(true);
        setIsSolving(false);
        setValue(name, captchaPayload);
        // Don't terminate — the module exports a singleton worker
        workerRef.current = null;
      };

      worker.onerror = (err: ErrorEvent) => {
        console.error('[CAPTCHA] Worker error:', err);
        setIsSolving(false);
        setError('Verification failed. Please refresh and try again.');
        workerRef.current = null;
      };

      // Send the raw challenge string to the worker for solving
      worker.postMessage(challengeData);
    } catch (err) {
      console.error('[CAPTCHA] Failed to solve challenge:', err);
      setIsSolving(false);
      setError('Verification failed. Please refresh and try again.');
    }
  }, [challengeId, challengeData, isSolving, isSolved, name, setValue]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      solveChallenge();
    }
  };

  return (
    <Width width={width ? width.toString() : undefined}>
      <div className={classes.wrap}>
        <div className={classes.captchaContainer}>
          {isLoading && <div className={classes.loading}>Loading verification...</div>}
          {error && <div className={classes.error}>{error}</div>}
          {!isLoading && !error && (
            <label className={classes.widgetContainer}>
              {isSolving ? (
                <div className={classes.widgetSpinner} />
              ) : (
                <input
                  type="checkbox"
                  onChange={handleCheckboxChange}
                  checked={isSolved}
                  disabled={isSolving || isSolved}
                  className={classes.widgetCheckbox}
                />
              )}
              <span className={classes.widgetText}>
                {isSolving
                  ? t('captcha.verifying')
                  : label || t('captcha.label')}
              </span>
              {isSolved && <span className={classes.widgetSuccess}>&#10003;</span>}
            </label>
          )}
        </div>
        {/* Hidden field to store the CAPTCHA data */}
        <input type="hidden" {...register(name, { required: requiredFromProps })} />
        {requiredFromProps && errors[name] && !isSolved && <Error />}
      </div>
    </Width>
  );
};
